import React, { useState, useEffect } from "react";
import { useSwipeable } from "react-swipeable";

import ICONS from "../../constants/icons";
import ICON_SIZE from "../icon/enums";

import { IconButton } from "../buttons";
import useWindowDimensions from "../../hooks/useWindowDimensions";

interface ISlider<T extends object> {
  arr: Array<T>;
  itemsPerPage: number;
  renderItem: (item: T, index: number) => React.ReactNode;
}

const Slider = <T extends object>({
  arr,
  itemsPerPage,
  renderItem,
}: ISlider<T>) => {
  const { isSmallScreen } = useWindowDimensions();
  const [startIndex, setStartIndex] = useState(0);
  const [responsiveItemsPerPage, setResponsiveItemsPerPage] =
    useState(itemsPerPage);
  const totalItems = arr.length;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setResponsiveItemsPerPage(1);
      } else if (window.innerWidth <= 768) {
        setResponsiveItemsPerPage(2);
      } else {
        setResponsiveItemsPerPage(itemsPerPage);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [itemsPerPage]);

  const nextSlide = () => {
    setStartIndex((prevIndex) => (prevIndex + 1) % totalItems);
  };

  const prevSlide = () => {
    setStartIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  };

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    trackMouse: true,
  });

  const rowClass = itemsPerPage > 3 ? "" : "row-small";

  return (
    <div
      className={`a-slider__carouselContainer row ${rowClass}`}
      {...handlers}
    >
      <div className="a-slider__carouselArrow -left">
        <IconButton
          handleClick={prevSlide}
          name={ICONS.LEFT}
          size={isSmallScreen ? ICON_SIZE.SIZE_32 : ICON_SIZE.SIZE_50}
          aria={{
            label: "Slider go left",
          }}
        />
      </div>

      <div className="a-slider__carouselWrapper">
        <div
          className="a-slider__carouselTrack"
          style={{
            transform: `translateX(-${
              startIndex * (100 / responsiveItemsPerPage)
            }%)`,
          }}
        >
          {arr.concat(arr).map((item, index) => (
            <div key={index}>{renderItem(item, index)}</div>
          ))}
        </div>
      </div>

      <div className="a-slider__carouselArrow -right">
        <IconButton
          handleClick={nextSlide}
          name={ICONS.RIGHT}
          size={isSmallScreen ? ICON_SIZE.SIZE_32 : ICON_SIZE.SIZE_50}
          aria={{
            label: "Slider go right",
          }}
        />
      </div>
    </div>
  );
};

export default Slider;
